/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import { useEffect, useState } from "react";
import Select, { StylesConfig, SingleValue } from 'react-select';
import MarkdownText from "components/common/MarkdownText";
import Background from "model/character/Background";
import { sortSourceGroups } from "model/formats/Open5eDocument";

interface BackgroundPageProps {
  selectedBackground: Background | null;
  onBackgroundChange: (background: Background | null) => void;
  documentSources: Map<string, { name: string; licenses: string[]; }>;
  backgrounds: Background[];
}

interface SelectOption {
  label: string;
  value: Background;
}

const BackgroundPage: React.FC<BackgroundPageProps> = ({
  selectedBackground,
  onBackgroundChange,
  documentSources,
  backgrounds
}: BackgroundPageProps) => {
  const [options, setOptions] = useState<{ label: string, options: SelectOption[] }[]>([]);

  // Fetch backgrounds for each source
  useEffect(() => {
    if (documentSources && backgrounds) {
      const backgroundsBySource = backgrounds.reduce((acc, background) => {
        const key = background.document__slug || 'default';
        if (!acc.has(key)) {
          acc.set(key, []);
        }
        acc.get(key)?.push({ value: background, label: background.name });
        return acc;
      }, new Map<string, SelectOption[]>());

      const formattedOptions = Array.from(documentSources.entries()).map(([key, license]) => {
        const backgroundsForSource = backgroundsBySource.get(key) || [];
        return { label: license.name, options: backgroundsForSource.sort((m1, m2) => m1.label.localeCompare(m2.label)) };
      });

      setOptions(sortSourceGroups(formattedOptions));
    }
  }, [documentSources, backgrounds]);

  const customStyles: StylesConfig<SelectOption, false> = {
    groupHeading: (base) => ({
      ...base,
      backgroundColor: '#f2f2f2',
      color: '#333',
      paddingLeft: '10px',
      padding: '5px',
      fontSize: '26px',
      fontWeight: 'bold',
      textTransform: 'none'
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      paddingLeft: '40px',
      paddingTop: '0px',
      paddingBottom: '0px',
      backgroundColor: isFocused ? 'lightgray' : 'white',
      color: 'black',
      fontWeight: isSelected ? 'bold' : 'normal',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (provided) => ({ ...provided, width: '350px', margin: '5px' })
  };

  const selectedOption = options.flatMap(option => option.options).find(opt => opt.value === selectedBackground);

  return (
    <>
      <div className="select-container" style={{ width: '100%' }}>
        <b>Select Background:</b>&nbsp;
        <Select
          menuPortalTarget={document.body}
          styles={customStyles}
          options={options}
          value={selectedOption}
          onChange={(selectedOption: SingleValue<SelectOption>) => { onBackgroundChange(selectedOption ? selectedOption.value : null); }}
          placeholder="Select a background"
          isClearable
          isSearchable
        />
      </div>
      {selectedBackground && (
        <>
          <br />
          <MarkdownText text={selectedBackground.desc} mon={null} />
          <table className="background-table">
            <tbody>
              <tr>
                <th>Skill Proficiencies</th>
                <td><MarkdownText text={selectedBackground.skill_proficiencies} mon={null} /></td>
              </tr>
              <tr>
                <th>Tool Proficiencies</th>
                <td><MarkdownText text={selectedBackground.tool_proficiencies || ''} mon={null} /></td>
              </tr>
              <tr>
                <th>Languages</th>
                <td><MarkdownText text={selectedBackground.languages} mon={null} /></td>
              </tr>
              <tr>
                <th>Equipment</th>
                <td><MarkdownText text={selectedBackground.equipment} mon={null} /></td>
              </tr>
              <tr>
                <th>Feature</th>
                <td><MarkdownText text={selectedBackground.feature} mon={null} /></td>
              </tr>
              <tr>
                <th>Feature Description</th>
                <td><MarkdownText text={selectedBackground.feature_desc} mon={null} /></td>
              </tr>
              <tr>
                <th>Suggested Characteristics</th>
                <td><MarkdownText text={selectedBackground.suggested_characteristics} mon={null} /></td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

export default BackgroundPage;
